import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';

export const calendarService = {
  /**
   * Get all events for a user within a date range
   */
  async getUserEvents(userId, startDate, endDate) {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));
      
      return { events, error: null };
    } catch (error) {
      console.error('Error getting user events:', error);
      return { events: [], error: error.message };
    }
  },

  /**
   * Get all global events (visible to all users)
   */
  async getGlobalEvents(startDate, endDate) {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('isGlobal', '==', true),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));
      
      return { events, error: null };
    } catch (error) {
      console.error('Error getting global events:', error);
      return { events: [], error: error.message };
    }
  },

  /**
   * Get all events for a specific month
   */
  async getEventsByMonth(userId, month, year) {
    try {
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59);
      
      // Get both user events and global events
      const [userEventsResult, globalEventsResult] = await Promise.all([
        this.getUserEvents(userId, startDate, endDate),
        this.getGlobalEvents(startDate, endDate)
      ]);
      
      if (userEventsResult.error || globalEventsResult.error) {
        return { 
          events: [], 
          error: userEventsResult.error || globalEventsResult.error 
        };
      }
      
      // Combine and sort events
      const allEvents = [...userEventsResult.events, ...globalEventsResult.events];
      allEvents.sort((a, b) => a.date - b.date);
      
      return { events: allEvents, error: null };
    } catch (error) {
      console.error('Error getting events by month:', error);
      return { events: [], error: error.message };
    }
  },

  /**
   * Create a new event
   */
  async createEvent(eventData) {
    try {
      const eventsRef = collection(db, 'events');
      const newEvent = {
        ...eventData,
        date: eventData.date instanceof Date ? Timestamp.fromDate(eventData.date) : eventData.date,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      const docRef = await addDoc(eventsRef, newEvent);
      return { eventId: docRef.id, error: null };
    } catch (error) {
      console.error('Error creating event:', error);
      return { eventId: null, error: error.message };
    }
  },

  /**
   * Update an existing event
   */
  async updateEvent(eventId, updates) {
    try {
      const eventRef = doc(db, 'events', eventId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
      };
      
      if (updates.date instanceof Date) {
        updateData.date = Timestamp.fromDate(updates.date);
      }
      
      await updateDoc(eventRef, updateData);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete an event
   */
  async deleteEvent(eventId) {
    try {
      const eventRef = doc(db, 'events', eventId);
      await deleteDoc(eventRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get upcoming deadlines for a user
   */
  async getUpcomingDeadlines(userId, limit = 5) {
    try {
      const eventsRef = collection(db, 'events');
      const now = Timestamp.now();
      const q = query(
        eventsRef,
        where('userId', '==', userId),
        where('type', '==', 'deadline'),
        where('date', '>=', now),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const deadlines = snapshot.docs.slice(0, limit).map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));
      
      return { deadlines, error: null };
    } catch (error) {
      console.error('Error getting upcoming deadlines:', error);
      return { deadlines: [], error: error.message };
    }
  },

  /**
   * Mark an event as completed
   */
  async markEventCompleted(eventId) {
    try {
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, {
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error marking event as completed:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get event by ID
   */
  async getEventById(eventId) {
    try {
      const eventRef = doc(db, 'events', eventId);
      const eventDoc = await getDoc(eventRef);
      
      if (!eventDoc.exists()) {
        return { event: null, error: 'Event not found' };
      }
      
      const event = {
        id: eventDoc.id,
        ...eventDoc.data(),
        date: eventDoc.data().date?.toDate(),
      };
      
      return { event, error: null };
    } catch (error) {
      console.error('Error getting event:', error);
      return { event: null, error: error.message };
    }
  },
};

